/**
 * Shared UI primitives — todos os building blocks do design system TITAM AI
 */
import { useState, useEffect, useRef } from 'react'
import Icon from './icons.jsx'
import { useApp } from '../context/AppContext.jsx'
import { fmtBRL, fmtNum, fmtPct, fmtMult } from '../db/api.js'

// ── Toast ─────────────────────────────────────────────────────────────────────
export function ToastContainer() {
  const { toasts } = useApp()
  return (
    <div className="toast-wrap">
      {toasts.map(t => (
        <div key={t.id} className="toast">
          <Icon n="check" s={14} c="#22c55e" />
          {t.msg}
        </div>
      ))}
    </div>
  )
}

// ── MetricCard ────────────────────────────────────────────────────────────────
function MetricCard({ label, value, sub, delta, deltaDir, format = 'raw', size = 'md' }) {
  const fmt = (v) => {
    if (v == null) return '—'
    if (format === 'brl')  return fmtBRL(v)
    if (format === 'num')  return fmtNum(v)
    if (format === 'pct')  return fmtPct(v)
    if (format === 'mult') return fmtMult(v)
    return v
  }
  return (
    <div className="metric-card">
      <div className="metric-label">{label}</div>
      <div className="metric-value" style={size === 'sm' ? { fontSize: 28 } : {}}>
        {fmt(value)}
      </div>
      {sub   && <div className="metric-sub">{sub}</div>}
      {delta != null && (
        <div className={`metric-delta ${deltaDir === 'up' ? 'delta-up' : 'delta-down'}`}>
          <Icon n={deltaDir === 'up' ? 'trend_up' : 'trend_dn'} s={12} />
          {delta}
        </div>
      )}
    </div>
  )
}

// ── SectionCard ───────────────────────────────────────────────────────────────
function SectionCard({ title, label, action, children, style }) {
  return (
    <div className="section-card" style={style}>
      <div className="sc-head">
        <div>
          {label && <div className="t-label" style={{ marginBottom: 4 }}>{label}</div>}
          {title && <div className="sc-title">{title}</div>}
        </div>
        {action}
      </div>
      <div className="sc-body">{children}</div>
    </div>
  )
}

// ── Badge ─────────────────────────────────────────────────────────────────────
function Badge({ children, variant = 'neutral', dot }) {
  return (
    <span className={`badge badge-${variant}`}>
      {dot && <span className={`dot dot-${dot}`} />}
      {children}
    </span>
  )
}

// ── InsightBanner ─────────────────────────────────────────────────────────────
function InsightBanner({ title, text, action }) {
  return (
    <div className="insight-banner">
      <div>
        <div className="insight-title">
          <Icon n="spark" s={14} c="#fff" /> {title}
        </div>
        <div className="insight-text">{text}</div>
      </div>
      {action && (
        <button className="btn btn-outline btn-sm" style={{ color: '#fff', borderColor: 'rgba(255,255,255,.3)' }}>
          {action}
        </button>
      )}
    </div>
  )
}

// ── Tabs ──────────────────────────────────────────────────────────────────────
function Tabs({ tabs, active, onChange }) {
  return (
    <div className="tab-bar">
      {tabs.map(t => (
        <div
          key={t.id}
          className={`tab ${active === t.id ? 'active' : ''}`}
          onClick={() => onChange(t.id)}
        >
          {t.icon && <Icon n={t.icon} s={14} />}
          {t.label}
          {t.badge != null && (
            <span className="sb-badge" style={{ marginLeft: 4 }}>{t.badge}</span>
          )}
        </div>
      ))}
    </div>
  )
}

// ── FilterDropdown ───────────────────────────────────────────────────────────
/**
 * Dropdown de filtro compartilhado. O menu é renderizado pela aplicação (não
 * pelo <select> do sistema operacional) e a caixa inteira funciona como alvo.
 */
export function FilterDropdown({
  label,
  icon,
  value = '',
  options = [],
  onChange,
  allLabel = 'Todos',
  allowAll = true,
  detail,
  disabled = false,
  emptyLabel = 'Nenhuma opção disponível.',
  className = '',
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const selected = options.find((option) => String(option.id) === String(value))

  useEffect(() => {
    if (!open) return undefined
    const closeOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false)
    }
    const closeWithEscape = (event) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', closeOutside)
    document.addEventListener('keydown', closeWithEscape)
    return () => {
      document.removeEventListener('mousedown', closeOutside)
      document.removeEventListener('keydown', closeWithEscape)
    }
  }, [open])

  useEffect(() => {
    if (disabled) setOpen(false)
  }, [disabled])

  const choose = (nextValue) => {
    onChange?.(String(nextValue))
    setOpen(false)
  }

  return (
    <div className={`filter-dropdown${open ? ' open' : ''}${disabled ? ' disabled' : ''}${className ? ` ${className}` : ''}`} ref={ref}>
      <button
        type="button"
        className="filter-dropdown-trigger"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
      >
        {icon && <Icon n={icon} s={18} />}
        <span className="filter-dropdown-copy">
          <span>{label}</span>
          <strong>{selected?.nome || allLabel}</strong>
          {detail && <small>{detail}</small>}
        </span>
        <Icon n="chevron_d" s={14} />
      </button>
      {open && (
        <div className="filter-dropdown-menu" role="listbox" aria-label={label}>
          <div className="filter-dropdown-list">
            {allowAll && (
              <button
                type="button"
                role="option"
                aria-selected={value === ''}
                className={`filter-dropdown-option${value === '' ? ' active' : ''}`}
                onClick={() => choose('')}
              >
                {allLabel}
              </button>
            )}
            {options.map((option) => (
              <button
                key={option.id}
                type="button"
                role="option"
                aria-selected={String(option.id) === String(value)}
                className={`filter-dropdown-option${String(option.id) === String(value) ? ' active' : ''}`}
                onClick={() => choose(option.id)}
              >
                {option.nome}
              </button>
            ))}
          </div>
          {!options.length && <div className="filter-dropdown-empty">{emptyLabel}</div>}
        </div>
      )}
    </div>
  )
}

// ── DataTable ─────────────────────────────────────────────────────────────────
function DataTable({ cols, rows, totals, loading }) {
  if (loading) return (
    <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--ter)', fontSize: 13 }}>
      Carregando...
    </div>
  )
  return (
    <div className="table-wrap">
      <table className="tbl">
        <thead>
          <tr>
            {cols.map((c, i) => (
              <th key={i} className={c.sep ? 'col-sep' : ''}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri}>
              {cols.map((c, ci) => (
                <td key={ci} className={c.sep ? 'col-sep' : ''}>
                  <span className={c.color?.(row[c.key]) || ''}>{row[c.key] ?? '—'}</span>
                </td>
              ))}
            </tr>
          ))}
          {totals && (
            <tr className="total-row">
              {cols.map((c, ci) => (
                <td key={ci} className={c.sep ? 'col-sep' : ''}>
                  {totals[c.key] ?? (ci === 0 ? 'Total' : '—')}
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

// ── FunnelRow ─────────────────────────────────────────────────────────────────
function FunnelRow({ label, value, pct, sub }) {
  return (
    <div className="funnel-row">
      <div style={{ width: 200, fontSize: 13, fontWeight: 500 }}>{label}</div>
      <div className="funnel-bar-wrap">
        <div className="funnel-bar" style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <div style={{ minWidth: 100, textAlign: 'right', fontWeight: 700, fontSize: 14 }}>{value}</div>
      {sub != null && (
        <div style={{ minWidth: 60, textAlign: 'right', fontSize: 12, color: 'var(--ter)' }}>{sub}</div>
      )}
    </div>
  )
}

// ── MiniSparkline ─────────────────────────────────────────────────────────────
function MiniSparkline({ data, color = 'var(--text)' }) {
  if (!data?.length) return null
  const max = Math.max(...data, 1)
  return (
    <div className="mini-bar-wrap">
      {data.map((v, i) => (
        <div
          key={i}
          className="mini-bar"
          style={{ height: `${Math.max((v / max) * 100, 4)}%`, background: color }}
        />
      ))}
    </div>
  )
}

// ── Period Picker ─────────────────────────────────────────────────────────────
// Helpers de data — mesmos cálculos do AppContext (preset = janela terminando hoje).
const _ppToday = () => new Date()
const _ppFmt = (d) => d.toISOString().split('T')[0]
const _ppDAgo = (n) => {
  const d = _ppToday()
  d.setDate(d.getDate() - n)
  return d
}

/**
 * PeriodPicker — calendário padrão do dashboard.
 *
 * Sem props → controla o período global via AppContext (uso na topbar).
 * Com `value` + `onChange` → modo controlado: opera sobre um intervalo local
 * (`{ start, end }`) sem tocar no estado global. Mesma aparência e
 * comportamento (presets + intervalo personalizado) nos dois modos.
 */
export function PeriodPicker({ value, onChange, openRight = false } = {}) {
  const app = useApp()
  const controlled = value != null && typeof onChange === 'function'
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  const period = controlled
    ? { start: value.start || '', end: value.end || '' }
    : app.period

  const presets = [
    { label: 'Hoje',         days: 1  },
    { label: 'Últimos 7 dias', days: 7  },
    { label: 'Últimos 14 dias', days: 14 },
    { label: 'Últimos 30 dias', days: 30 },
    { label: 'Últimos 60 dias', days: 60 },
    { label: 'Últimos 90 dias', days: 90 },
  ]

  // No modo controlado o rótulo é derivado: casa com um preset ou cai em
  // "Personalizado" / "Selecionar período" (vazio).
  const matchedPreset = controlled
    ? presets.find(
        (p) => period.start === _ppFmt(_ppDAgo(p.days - 1)) && period.end === _ppFmt(_ppToday()),
      )
    : null
  const periodLabel = controlled
    ? matchedPreset?.label || (period.start || period.end ? 'Personalizado' : 'Selecionar período')
    : app.periodLabel

  const applyPreset = (label, days) => {
    if (controlled) {
      onChange({ start: _ppFmt(_ppDAgo(days - 1)), end: _ppFmt(_ppToday()) })
    } else {
      app.setPeriodPreset(label, days)
    }
    setOpen(false)
  }

  const applyCustom = (key, val) => {
    if (controlled) {
      onChange({ ...period, [key]: val })
    } else {
      app.setPeriod((p) => ({ ...p, [key]: val }))
      app.setPeriodLabel('Personalizado')
    }
  }

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <button className="tb-period" onClick={() => setOpen(o => !o)} type="button">
        <Icon n="calendar" s={14} />
        {periodLabel}
        <Icon n="chevron_d" s={12} />
      </button>
      {open && (
        <div style={{
          // openRight: ancora à esquerda do botão e expande p/ a DIREITA
          // (usado quando o picker fica na borda esquerda do conteúdo, p/ não
          // abrir por baixo/sobre a sidebar). Padrão: expande p/ a esquerda
          // (picker no canto direito da topbar).
          position: 'absolute', top: '100%',
          left: openRight ? 0 : 'auto', right: openRight ? 'auto' : 0, marginTop: 6,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 16, padding: 8, minWidth: 200, zIndex: 200,
          boxShadow: '0 8px 32px rgba(0,0,0,.10)',
        }}>
          {presets.map(p => (
            <button key={p.label} type="button" onClick={() => applyPreset(p.label, p.days)}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '9px 14px', borderRadius: 10, fontSize: 13,
                fontWeight: periodLabel === p.label ? 600 : 400,
                color: periodLabel === p.label ? 'var(--text)' : 'var(--sec)',
                background: periodLabel === p.label ? 'var(--bg)' : 'transparent',
              }}>
              {p.label}
            </button>
          ))}
          <div style={{ height: 1, background: 'var(--soft)', margin: '8px 0' }} />
          <div style={{ padding: '4px 14px 8px', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ter)', textTransform: 'uppercase', letterSpacing: '.08em' }}>Período personalizado</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="date" className="input" style={{ fontSize: 12, padding: '7px 10px' }}
                value={period.start}
                onChange={e => applyCustom('start', e.target.value)}
              />
              <span style={{ color: 'var(--ter)', fontSize: 12 }}>até</span>
              <input type="date" className="input" style={{ fontSize: 12, padding: '7px 10px' }}
                value={period.end}
                onChange={e => applyCustom('end', e.target.value)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── AccountFilter ─────────────────────────────────────────────────────────────
function AccountFilter() {
  const { accounts, selectedAccounts, setSelectedAccounts } = useApp()
  const toggle = (id) => setSelectedAccounts(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  )
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
      <span style={{ fontSize: 12, color: 'var(--ter)', fontWeight: 600 }}>Conta:</span>
      <button
        className={`pill pill-sm ${selectedAccounts.length === 0 ? 'active' : ''}`}
        onClick={() => setSelectedAccounts([])}
      >Todas</button>
      {accounts.map(a => (
        <button
          key={a.id}
          className={`pill pill-sm ${selectedAccounts.includes(a.id) ? 'active' : ''}`}
          onClick={() => toggle(a.id)}
        >
          {a.name.split('—')[0].trim()}
        </button>
      ))}
    </div>
  )
}

// ── Empty ─────────────────────────────────────────────────────────────────────
function Empty({ icon = 'chart', title = 'Sem dados', desc = 'Nenhum dado encontrado para o período selecionado.' }) {
  return (
    <div className="empty">
      <div className="empty-icon"><Icon n={icon} s={22} c="var(--ter)" /></div>
      <div className="empty-title">{title}</div>
      <div className="empty-desc">{desc}</div>
    </div>
  )
}
